import {
  MAX_PERSISTED_SIDEBAR_SECTIONS,
  SIDEBAR_COLLAPSED_SECTIONS_STORAGE_KEY,
} from "../constants/local-storage";
import { parseStoredCollapsedSidebarSections } from "../parser/sidebar-collapsed-sections";
import type {
  IIsSidebarSectionOpenProps,
  TSidebarCollapsedSections,
} from "../types/sidebar-sections";
import type { IStorageAdapter } from "./storage";

interface IReadCollapsedSidebarSectionsProps {
  storage: IStorageAdapter;
}

interface ISetCollapsedSidebarSectionsProps {
  storage: IStorageAdapter;
  // NOTE: the keys the calling block renders. Only these are rewritten —
  // everything else in storage belongs to another block (or to a page of data
  // not loaded yet) and is carried through untouched, so every collapsible
  // block in the sidebar can share one storage entry without clobbering the
  // others.
  sectionKeys: readonly string[];
  collapsedSectionKeys: readonly string[];
}

// NOTE: NSAI-5926 — device storage can throw rather than merely answer empty:
// Safari in private mode and a browser set to block site data both raise on
// access, and the mobile adapter writes through AsyncStorage. A sidebar that
// cannot remember a collapsed section is a lost preference; a sidebar that
// throws while rendering is a blank page, so every access is guarded and the
// failure degrades to "nothing remembered".
export const readCollapsedSidebarSections = ({
  storage,
}: IReadCollapsedSidebarSectionsProps): TSidebarCollapsedSections => {
  try {
    return parseStoredCollapsedSidebarSections(
      storage.getItem(SIDEBAR_COLLAPSED_SECTIONS_STORAGE_KEY),
    );
  } catch (error) {
    console.error("Error reading the collapsed sidebar sections:", error);

    return {};
  }
};

// NOTE: NSAI-5926 — rewrites only the keys the calling block owns and returns
// the merged record, so the caller's React state and storage never drift apart.
// Owned keys are re-appended rather than updated in place, which makes the
// record's insertion order a recency order and lets the cap drop the least
// recently touched entries first.
export const setCollapsedSidebarSections = ({
  storage,
  sectionKeys,
  collapsedSectionKeys,
}: ISetCollapsedSidebarSectionsProps): TSidebarCollapsedSections => {
  const ownedKeys = new Set(sectionKeys);
  const collapsedKeys = new Set(collapsedSectionKeys);
  const storedSections = readCollapsedSidebarSections({ storage });

  const ownedEntries = sectionKeys.map((sectionKey): [string, boolean] => [
    sectionKey,
    collapsedKeys.has(sectionKey),
  ]);
  const carriedEntries = Object.entries(storedSections).filter(
    ([key]) => !ownedKeys.has(key),
  );

  // NOTE: NSAI-5926 (review follow-up) — the cap is spent on the CARRIED
  // entries, never on the keys this write owns. Capping the concatenation
  // instead would evict by position, so a caller rendering more than
  // MAX_PERSISTED_SIDEBAR_SECTIONS keys at once would silently drop some of
  // the state it just wrote — the opposite of the eviction order above. No
  // section type comes close to that today; making the guarantee hold for
  // every input shape is cheaper than relying on that staying true.
  const carriedBudget = Math.max(
    0,
    MAX_PERSISTED_SIDEBAR_SECTIONS - ownedEntries.length,
  );
  // HACK: `slice(-0)` returns the whole array rather than none of it, so a
  // spent budget is spelled out instead of passed to slice.
  const keptCarriedEntries =
    carriedBudget === 0 ? [] : carriedEntries.slice(-carriedBudget);

  const nextSections: TSidebarCollapsedSections = Object.fromEntries([
    ...keptCarriedEntries,
    ...ownedEntries,
  ]);

  try {
    storage.setItem(
      SIDEBAR_COLLAPSED_SECTIONS_STORAGE_KEY,
      JSON.stringify(nextSections),
    );
  } catch (error) {
    console.error("Error persisting the collapsed sidebar sections:", error);
  }

  return nextSections;
};

// NOTE: NSAI-5926 — the reading half of the record: a section is open unless
// the user collapsed it, except for the blocks that ship closed (the archive),
// which pass isCollapsedByDefault and stay closed until explicitly opened.
export const isSidebarSectionOpen = ({
  collapsedSections,
  sectionKey,
  isCollapsedByDefault = false,
}: IIsSidebarSectionOpenProps): boolean => {
  const isCollapsed = collapsedSections[sectionKey];

  return isCollapsed === undefined ? !isCollapsedByDefault : !isCollapsed;
};
